import { type ReactNode, useEffect, useRef } from 'react';
import { Button } from './button.tsx';

/** 네이티브 <dialog> — 포커스 가두기·Esc 를 브라우저가 한다. Radix 없이 */
export function Dialog({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      onClose={onClose}
      className="m-auto w-full max-w-lg rounded-lg p-0 shadow-xl backdrop:bg-slate-900/40"
    >
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <h3 className="font-semibold">{title}</h3>
        <Button variant="ghost" size="icon" onClick={onClose} aria-label="닫기">
          ✕
        </Button>
      </div>
      <div className="p-4">{open ? children : null}</div>
    </dialog>
  );
}

/**
 * 오른쪽에서 밀려 들어오는 패널 — `Dialog` 와 같은 네이티브 `<dialog>` 라 포커스 가두기·Esc 를 브라우저가 한다.
 * 들어오는 움직임은 `@starting-style`(Tailwind `starting:`)이다 — 열릴 때만 미끄러지고 닫힘은 즉시다.
 * 바깥(backdrop)을 누르면 닫힌다: backdrop 클릭은 `<dialog>` 자신이 target 이 되는 것으로 가른다
 */
export function SidePanel({
  open,
  onClose,
  title,
  description,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: 키보드는 네이티브 <dialog> 의 Esc 가 맡는다
    <dialog
      ref={ref}
      onClose={onClose}
      onClick={(e) => e.target === e.currentTarget && onClose()}
      className="fixed inset-y-0 right-0 left-auto m-0 h-dvh max-h-none w-full max-w-3xl translate-x-0 bg-white p-0 shadow-2xl transition-transform duration-200 ease-out backdrop:bg-slate-900/30 starting:translate-x-full"
    >
      <div className="flex h-full flex-col">
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div className="min-w-0">
            <h3 className="font-semibold">{title}</h3>
            {description ? <p className="mt-0.5 text-sm text-slate-500">{description}</p> : null}
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="닫기">
            ✕
          </Button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">{open ? children : null}</div>
      </div>
    </dialog>
  );
}
